import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Radar, Loader2, ExternalLink, XCircle, KeyRound, Building2, MapPin, Sparkles, Telescope, Bot, Check, Search } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { tDynamic } from "@/i18n/dynamic";
import { fmtDateTime } from "@/i18n/format";
import { PageHeader, EmptyState, Pill, STATUS_TONE } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/CandidateCard";
import CandidatePanel from "@/components/CandidatePanel";
import { EnrichButton } from "@/components/EnrichButton";
import { BulkEnrichBar, BULK_MAX } from "@/components/BulkEnrichBar";
import { CvAnalysis } from "@/components/CvAnalysis";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const JOB_LABEL = { live_discovery: tk("Live discovery"), deep_search: tk("Deep search"), agent_research: tk("Agent research"),
  contact_enrichment: tk("Contact enrichment"), email_enrichment: tk("Email enrichment"), phone_enrichment: tk("Phone enrichment") };
export const STATUS_LABEL = { queued: tk("Queued"), running: tk("Running"), completed: tk("Completed"), failed: tk("Failed"), cancelled: tk("Cancelled"), blocked: tk("Blocked") };
const MODES = [
  { id: "live", icon: Radar, title: tk("Live discovery"), desc: tk("3 targeted AI queries, ~30 scored profiles in about a minute.") },
  { id: "deep", icon: Telescope, title: tk("Deep search"), desc: tk("6 broadened queries (synonyms, nearby cities), up to 150 profiles analysed.") },
  { id: "agent", icon: Bot, title: tk("Agent research"), desc: tk("An agent explores the web and checks each profile. 5 to 15 min.") },
];
const STAGES = { planning: tk("Planning queries"), searching: tk("Searching profiles"), agent: tk("The agent is exploring the web"), scoring: tk("AI scoring of profiles"), done: tk("Done") };
const scoreTone = (s) => (s >= 75 ? "green" : s >= 50 ? "amber" : "default");
const running = (j) => ["queued", "running"].includes(j?.status);
const ENRICH_JOBS = ["contact_enrichment", "email_enrichment", "phone_enrichment"];

function Launcher({ status, onLaunched }) {
  const { setUpgradeOpen, refresh } = useAuth();
  const { t } = useI18n();
  const [brief, setBrief] = useState("");
  const [mode, setMode] = useState("live");
  const [busy, setBusy] = useState(false);
  const disabled = busy || brief.trim().length < 5 || !status?.configured;
  const launch = async () => {
    setBusy(true);
    try {
      const { data: p } = await api.post("/search/parse", { text: brief });
      const { data } = await api.post("/ai/discover", { intent: p.intent, effort: mode, brief, idempotency_key: `${Date.now()}` });
      toast.success(t("{{mode}} started — {{count}} credits reserved", { mode: t(MODES.find((m) => m.id === mode).title), count: data.reserved_credits }));
      refresh?.(); onLaunched(data);
    } catch (e) {
      if (["upgrade_required", "insufficient_credits"].includes(errorCode(e))) setUpgradeOpen(true); else toast.error(errorDetail(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-5 mb-6" data-testid="discovery-launcher">
      <div className="eyebrow mb-2">{t("New discovery")}</div>
      <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} data-testid="discovery-brief-input"
        placeholder={t("Describe the ideal profile… e.g. Industrial maintenance manager, 8+ years, food industry, around Lille (50 km), professional English")} />
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        {MODES.map((m) => (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`discovery-mode-${m.id}`}
            className={`text-left rounded-xl border p-3 transition-colors ${mode === m.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}>
            <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-sm font-semibold"><m.icon className="w-4 h-4 text-primary" />{t(m.title)}</span>
              <span className="text-xs font-mono font-bold">{t("{{count}} cr.", { count: status?.tariffs?.[m.id] ?? "…" })}</span></div>
            <p className="text-xs text-muted-foreground mt-1">{t(m.desc)}</p>
          </button>
        ))}
      </div>
      <div className="flex justify-between items-center mt-3 gap-3">
        <p className="text-[11px] text-muted-foreground">{t("Credits are reserved at launch and refunded if no profile is found. Every profile gets an explained AI score.")}</p>
        <Button onClick={launch} disabled={disabled} data-testid="discovery-launch-button" className="shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}{t("Launch")}
        </Button>
      </div>
    </div>
  );
}

function LeadCard({ l, i, onOpen, checked, onCheck }) {
  const { t } = useI18n();
  const locked = running(l.enrichment) || l.enrichment?.contact_status === "found" || running(l.enrichment_email) || running(l.enrichment_phone);
  return (
    <div className="card-surface p-3 flex gap-3 cursor-pointer hover:border-primary/40" onClick={() => onOpen(l.candidate_id)} data-testid={`lead-${i}`}>
      <div onClick={(e) => e.stopPropagation()} className="pt-2"><Checkbox checked={checked} disabled={locked} onCheckedChange={onCheck} data-testid={`lead-select-${i}`} /></div>
      <Avatar name={l.name} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2"><b className="truncate text-sm" data-testid={`lead-name-${i}`}>{l.name}</b>
          <div className="flex gap-1 shrink-0">{l.score != null && <Pill tone={scoreTone(l.score)} testid={`lead-score-${i}`}>{l.score}/100</Pill>}
            <Pill tone={l.already_in_catalog ? "default" : "green"}>{l.already_in_catalog ? t("Already in catalog") : t("Added")}</Pill></div></div>
        <div className="text-xs text-muted-foreground truncate">{l.title || t("Title unknown")}</div>
        <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground mt-1">
          {l.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{l.company}</span>}
          {l.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.location}</span>}
        </div>
        {l.reason && <p className="text-xs mt-1.5 flex gap-1.5" data-testid={`lead-reason-${i}`}><Sparkles className="w-3 h-3 text-primary shrink-0 mt-0.5" />{l.reason}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
          <EnrichButton candidateId={l.candidate_id} enrichment={l.enrichment_email} kind="email" testid={`email-enrich-lead-${i}`} />
          <EnrichButton candidateId={l.candidate_id} enrichment={l.enrichment_phone} kind="phone" testid={`phone-enrich-lead-${i}`} />
          <a href={l.url} target="_blank" rel="noreferrer" className="h-7 px-2.5 rounded-md border text-xs font-medium flex items-center gap-1 hover:bg-muted"><ExternalLink className="w-3.5 h-3.5" />{t("Profile")}</a>
        </div>
      </div>
    </div>
  );
}

function Progress({ job }) {
  const { t } = useI18n();
  const p = job.progress || {};
  const order = job.job_type === "agent_research" ? ["agent", "scoring", "done"] : ["planning", "searching", "scoring", "done"];
  const cur = job.status === "completed" ? order.length - 1 : Math.max(0, order.indexOf(p.stage));
  return (
    <div className="flex flex-wrap gap-2" data-testid="ai-job-progress">
      {order.map((s, i) => (
        <span key={s} className={`flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border ${i < cur ? "text-emerald-700 border-emerald-200" : i === cur ? "text-primary border-primary" : "text-muted-foreground"}`}>
          {i < cur ? <Check className="w-3 h-3" /> : i === cur && running(job) ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {t(STAGES[s])}{s === "searching" && p.queries_total ? ` ${p.queries_done}/${p.queries_total}` : ""}
        </span>
      ))}
    </div>
  );
}

function JobDetail({ sel, leads, picked, setPicked, toggle, reload, cancel, setPanel }) {
  const { t } = useI18n();
  const queries = sel.output?.queries || [];
  const isEnrich = ENRICH_JOBS.includes(sel.job_type);
  return (
    <div className="card-surface p-5 space-y-4" data-testid="ai-job-detail">
      <div className="flex justify-between gap-3"><div><div className="font-semibold text-lg" data-testid="ai-job-title">{sel.title || t(JOB_LABEL[sel.job_type] || sel.job_type)}</div><div className="text-xs text-muted-foreground">{t(JOB_LABEL[sel.job_type] || sel.job_type)}</div></div>
        {running(sel) && <Button size="sm" variant="outline" onClick={() => cancel(sel)} data-testid="cancel-ai-job"><XCircle className="w-4 h-4" />{t("Cancel")}</Button>}</div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid="ai-job-summary"><Pill tone={STATUS_TONE[sel.status]}>{t(STATUS_LABEL[sel.status] || sel.status)}</Pill>
        <span>{t("{{count}} profiles", { count: leads.length })}</span><span>{t("{{count}} credits", { count: sel.charged_credits })}</span><span>{fmtDateTime(sel.created_at)}</span></div>
      {sel.error && <p className="text-xs text-red-600" data-testid="ai-job-error">{tDynamic(sel.error)}</p>}
      {sel.input?.brief && <p className="text-sm italic text-muted-foreground" data-testid="ai-job-brief">« {sel.input.brief} »</p>}
      {!isEnrich && <Progress job={sel} />}
      {queries.length > 0 && sel.job_type !== "agent_research" && (
        <details className="text-xs" data-testid="ai-job-queries"><summary className="cursor-pointer text-muted-foreground">{t("AI-generated queries ({{count}})", { count: queries.length })}</summary>
          <ul className="mt-2 space-y-1 list-disc pl-5">{queries.map((q) => <li key={q}>{q}</li>)}</ul></details>
      )}
      <div><div className="eyebrow mb-2">{t("Profiles · sorted by AI score · saved to your catalog")}</div>
        <BulkEnrichBar leads={leads} selected={picked} setSelected={setPicked} onQueued={reload} />
        <div className="space-y-2">{leads.map((l, i) => <LeadCard key={l.candidate_id} l={l} i={i} onOpen={setPanel} checked={picked.has(l.candidate_id)} onCheck={() => toggle(l.candidate_id)} />)}</div>
        {!leads.length && !running(sel) && <p className="text-sm text-muted-foreground">{t("No profiles returned.")}</p>}
      </div>
    </div>
  );
}

function JobRow({ j, active, onOpen }) {
  const { t } = useI18n();
  return (
    <button onClick={() => onOpen(j)} data-testid={`ai-job-${j.id}`} className={`w-full text-left card-surface p-3 hover:border-primary/40 ${active ? "border-primary" : ""}`}>
      <div className="flex justify-between items-center gap-2"><span className="text-sm font-medium truncate" title={j.title || ""}>{j.title || t(JOB_LABEL[j.job_type] || j.job_type)}</span>
        <Pill tone={STATUS_TONE[j.status]}>{running(j) && <Loader2 className="w-3 h-3 animate-spin" />}{t(STATUS_LABEL[j.status] || j.status)}</Pill></div>
      <div className="text-xs text-muted-foreground mt-1">{t(JOB_LABEL[j.job_type] || j.job_type)} · {t("{{count}} profiles", { count: j.lead_count })} · {fmtDateTime(j.created_at)}</div>
      {j.error && <div className="text-xs text-red-600 mt-1 truncate">{tDynamic(j.error)}</div>}
    </button>
  );
}

export default function AIDiscovery() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [sel, setSel] = useState(null);
  const [panel, setPanel] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const leads = (sel?.output?.leads || []).filter((l) => l.candidate_id);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else if (n.size < BULK_MAX) n.add(id); else toast.info(t("Maximum {{max}} profiles per batch", { max: BULK_MAX })); return n; });
  const load = () => api.get("/ai/jobs").then((r) => setJobs(r.data.filter((j) => !ENRICH_JOBS.includes(j.job_type))));
  const open = (j) => api.get(`/ai/jobs/${j.id}`).then((r) => { setSel(r.data); if (r.data.id !== sel?.id) setPicked(new Set()); });
  const [params] = useSearchParams();
  useEffect(() => { api.get("/ai/status").then((r) => setStatus(r.data)); load(); if (params.get("job")) open({ id: params.get("job") }); }, []); // eslint-disable-line
  useEffect(() => {
    if (!sel || !(running(sel) || leads.some((l) => running(l.enrichment) || running(l.enrichment_email) || running(l.enrichment_phone)))) return;
    const h = setTimeout(() => open(sel), 4000);
    return () => clearTimeout(h);
  }, [sel]); // eslint-disable-line
  useEffect(() => {
    if (!jobs.some(running)) return;
    const h = setTimeout(load, 4000);
    return () => clearTimeout(h);
  }, [jobs]);
  const cancel = async (j) => { try { await api.post(`/ai/jobs/${j.id}/cancel`); load(); open(j); } catch (e) { toast.error(errorDetail(e)); } };
  const launched = (j) => { load(); open(j); };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow="TopTalent Discovery Engine" title={t("AI Discovery workspace")} desc={t("Describe your need: the AI writes the queries, finds the profiles, scores them and explains why.")} />
      {status && !status.configured && (
        <div className="card-surface p-5 mb-6 border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 flex gap-3" data-testid="exa-not-configured-banner">
          <KeyRound className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="text-sm"><b>{t("Discovery Engine not activated.")}</b> {t("Discovery will be available as soon as an administrator activates it. No discovery is ever simulated.")}</div>
        </div>
      )}
      <Launcher status={status} onLaunched={launched} />
      <CvAnalysis tariff={status?.tariffs?.cv} onOpen={setPanel} />
      <div className="grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2" data-testid="ai-job-list">
          {jobs.length === 0 && <EmptyState icon={Radar} title={t("No AI jobs yet")} body={t("Launch your first discovery above.")} testid="no-ai-jobs" />}
          {jobs.map((j) => <JobRow key={j.id} j={j} active={sel?.id === j.id} onOpen={open} />)}
        </div>
        <div className="lg:col-span-3">
          {sel ? <JobDetail sel={sel} leads={leads} picked={picked} setPicked={setPicked} toggle={toggle} reload={() => open(sel)} cancel={cancel} setPanel={setPanel} />
            : <EmptyState icon={Radar} title={t("Select a job")} body={t("Review the profiles found, their scores and costs.")} />}
        </div>
      </div>
      {panel && <aside className="fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] bg-background border-l shadow-xl overflow-y-auto" data-testid="lead-detail-panel"><CandidatePanel id={panel} onClose={() => setPanel(null)} /></aside>}
    </div>
  );
}
